// modules/helpdesk/redux/ticketSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { ticketAxios } from "../services/ticketAxios";
import { createTicket, fetchTickets, fetchTicketDetails } from "./thunk/ticketThunk";

// Types
interface StatusItem {
  statusID: number;
  name: string;
}

interface Ticket {
  id: number;
  ticketID: number;
  ticketNumber: string;
  issueType?: {
    name: string;
  };
  issueDetails?: {
    issueDetails: string;
  };
  createdAt: string;
  status?: {
    name: string;
  };
}

interface TicketState {
  tickets: Ticket[];
  statusList: StatusItem[];
  totalItems: number;
  currentPage: number;
  rowsPerPage: number;
  searchTerm: string;
  loading: boolean;
  error: string | null;
  filters: {
    status: string;
    fromDate: string | null;
    toDate: string | null;
  };
  lastFetched: number | null; // timestamp for cache invalidation
  ticketDetails: any | null; // Store single ticket details
  ticketDetailsLoading: boolean;
}

interface FetchTicketsParams {
  page: number;
  rowsPerPage: number;
  searchTerm: string;
  filters: {
    status: string;
    fromDate: string | null;
    toDate: string | null;
  };
  mobile: string;
}

const initialState: TicketState = {
  tickets: [],
  statusList: [],
  totalItems: 0,
  currentPage: 1,
  rowsPerPage: 20,
  searchTerm: "",
  loading: false,
  error: null,
  filters: {
    status: "",
    fromDate: null,
    toDate: null,
  },
  lastFetched: null,
  ticketDetails: null,
  ticketDetailsLoading: false,
};

// Slice
const ticketSlice = createSlice({
  name: "tickets",
  initialState,
  reducers: {
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
      state.currentPage = 1; // Reset to first page on search
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setRowsPerPage: (state, action: PayloadAction<number>) => {
      state.rowsPerPage = action.payload;
      state.currentPage = 1; // Reset to first page when rows per page changes
    },
    setFilters: (state, action: PayloadAction<TicketState["filters"]>) => {
      state.filters = action.payload;
      state.currentPage = 1; // Reset to first page when filters change
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
      state.currentPage = 1;
    },
    clearTickets: (state) => {
      state.tickets = [];
      state.totalItems = 0;
      state.lastFetched = null;
    },
    invalidateTicketsCache: (state) => {
      state.lastFetched = null;
    },
    clearTicketDetails: (state) => {
      state.ticketDetails = null;
      state.ticketDetailsLoading = false;
    },
  },
  extraReducers: (builder) => {
    // Fetch Tickets
    builder
      .addCase(fetchTickets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTickets.fulfilled, (state, action) => {
        state.loading = false;
        state.tickets = action.payload.tickets;
        state.statusList = action.payload.statusList;
        state.totalItems = action.payload.totalItems;
        state.currentPage = action.payload.currentPage;
        state.lastFetched = Date.now();
      })
      .addCase(fetchTickets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.tickets = [];
        state.totalItems = 0;
      });

    // Create Ticket
    builder
      .addCase(createTicket.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTicket.fulfilled, (state, action) => {
        state.loading = false;
        // Option 1: Add the new ticket to the beginning of the list
        state.tickets.unshift(action.payload);
        state.totalItems += 1;

        // Invalidate cache to ensure fresh data on next fetch
        state.lastFetched = null;
      })
      .addCase(createTicket.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch Ticket Details
    builder
      .addCase(fetchTicketDetails.pending, (state) => {
        state.ticketDetailsLoading = true;
        state.error = null;
      })
      .addCase(fetchTicketDetails.fulfilled, (state, action) => {
        state.ticketDetailsLoading = false;
        state.ticketDetails = action.payload;
      })
      .addCase(fetchTicketDetails.rejected, (state, action) => {
        state.ticketDetailsLoading = false;
        state.error = action.payload as string;
        state.ticketDetails = null;
      });
  },
});

export const {
  setSearchTerm,
  setPage,
  setRowsPerPage,
  setFilters,
  resetFilters,
  clearTickets,
  invalidateTicketsCache,
  clearTicketDetails,
} = ticketSlice.actions;

export default ticketSlice.reducer;
